/*
 * Copyright (c) 2024. Dave van Rijn Development
 */

import {DefaultPDFElementParams, IndexedObject, PdfFont, PDFTextVariables, SelectItemShape} from "./interfaces";

export const mmToPt = (mm: number) => Math.round(pxToPt(mmToPx(mm)));
export const mmToPx = (mm: number, toTens: boolean = true) => {
    const value: number = Math.round(mm / 0.264583333);
    return toTens ? roundToTens(value) : value;
}
export const pxToMm = (pixels: number) => Math.round(pixels * 0.264583333);
export const pxToPt = (px: number) => Math.round(px * .75);
export const ptToPx = (pt: number, toTens: boolean = true) => {
    const value: number = Math.round(pt / .75);
    return toTens ? roundToTens(value) : value;
}
export const ptToMm = (pt: number) => pxToMm(ptToPx(pt));
export const PAGE_WIDTH = 794, PAGE_HEIGHT = 1122; // In pixels

export const PDF_VARIABLES: PDFTextVariables = {
    company: {
        bedrijf_naam: 'Bedrijfsnaam',
        bedrijf_adres: 'Adres',
        bedrijf_postcode: 'Postcode',
        bedrijf_stad: 'Stad',
        bedrijf_land: 'Land',
        bedrijf_email: 'E-mailadres',
        bedrijf_telefoon: 'Telefoonnummer',
        bedrijf_iban: 'IBAN',
        bedrijf_btw: 'BTW-nummer',
        bedrijf_kvk: 'KvK nummer'
    },
    client: {
        klant_naam: 'Klantnaam',
        klant_adres: 'Adres',
        klant_postcode: 'Postcode',
        klant_stad: 'Stad',
        klant_land: 'Land',
        klant_extra: 'Adres toevoeging',
        klant_email: 'E-mailadres',
        klant_telefoon: 'Telefoonnummer',
        // klant_iban: 'IBAN',
        klant_btw: 'BTW-nummer',
        klant_kvk: 'KvK nummer',
        klant_bedrijf: 'Bedrijfsnaam',
    },
    invoice: {
        datum: 'Huidige datum',
        factuurnummer: 'Factuurnummer',
        subtotaal: 'Subtotaal',
        btw_totaal: 'Totaal BTW (\u20ac)',
        factuurtotaal: 'Factuurtotaal',
        factuurdatum: 'Factuur datum',
        factuurref: 'Factuur referentie'
    }
}

export const getPdfVariables = (customVariables?: PDFTextVariables): PDFTextVariables => {
    const variables = PDF_VARIABLES;
    if (customVariables) {
        const {company, invoice, client} = customVariables;
        for (const key of Object.keys(company))
            variables.company[key] = company[key];
        for (const key of Object.keys(invoice))
            variables.invoice[key] = invoice[key];
        for (const key of Object.keys(client))
            variables.client[key] = client[key];
    }
    return variables;
}

export const setPdfVariables = (text: string, values: IndexedObject<string>) => {
    for (const key of Object.keys(values)) {
        const value = values[key];
        if (text.includes(`{${key}}`) && value.length) text =
            text.replace(new RegExp('\{' + key + '\}', 'g'), value);
    }
    return text;
};

export const setElementSettings = (element: DefaultPDFElementParams<any, any>, convertUnits: boolean = true) => {
    const {dimensions, options} = element;
    if (convertUnits) {
        if (dimensions?.width) dimensions.width = ptToPx(dimensions.width, false);
        if (dimensions?.height) dimensions.height = ptToPx(dimensions.height, false);
        if (options?.fontSize) options.fontSize = ptToPx(element.options.fontSize, false);
    }
};

export const fontItems: SelectItemShape[] = [
    {label: 'Helvetica', value: PdfFont.HELVETICA, labelStyle: {fontFamily: 'helvetica'}},
    {label: 'Lato', value: PdfFont.LATO, labelStyle: {fontFamily: 'lato'}},
    {label: 'Merriweather', value: PdfFont.MERRIWEATHER, labelStyle: {fontFamily: 'merriweather'}},
    {label: 'Noto Sans', value: PdfFont.NOTO_SANS, labelStyle: {fontFamily: 'noto-sans'}},
    {label: 'Noto Serif', value: PdfFont.NOTO_SERIF, labelStyle: {fontFamily: 'noto-serif'}},
    {label: 'Open Sans', value: PdfFont.OPEN_SANS, labelStyle: {fontFamily: 'open-sans'}},
    {label: 'Playfair Display', value: PdfFont.PLAYFAIR, labelStyle: {fontFamily: 'playfair'}},
    {label: 'PT Serif', value: PdfFont.PT_SERIF, labelStyle: {fontFamily: 'pt-serif'}},
    {label: 'Roboto', value: PdfFont.ROBOTO, labelStyle: {fontFamily: 'roboto'}},
    {label: 'Roboto Mono', value: PdfFont.ROBOTO_MONO, labelStyle: {fontFamily: 'roboto-mono'}},
    {label: 'Source Sans Pro', value: PdfFont.SOURCE_SANS_PRO, labelStyle: {fontFamily: 'source-sans-pro'}}
];

export const parsePrice = (price: number) => {
    return price.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const roundToTens = (value: number): number => {
    const remainder = value % 10;
    if (remainder === 0) return value;
    if (remainder > 4) return value + (10 - remainder);
    return value - remainder;
};

export const waitForFonts = (callback: Function) => {
    // @ts-ignore
    if (document.fonts && document.fonts.ready) {
        // @ts-ignore
        document.fonts.ready.then(() => {
            callback();
        });
    }
};